import type { Direction, Turn } from './direction';
import type { CutWidth, Side } from './footprint';
import { initialTurnRuleState, type TurnRuleSpec, type TurnRuleState } from './turnRules';
import type { BoostId } from './boosts';
import type { ItemId } from './items';

/** Static description of a mower model. Defined in content, consumed by the sim. */
export interface MowerSpec {
  id: string;
  name: string;
  /** Ticks to travel one tile (lower is faster). At 60 Hz, 30 = 2 tiles/second. */
  ticksPerTile: number;
  /** Hits the mower can take; it breaks when this reaches 0. */
  maxHp: number;
  /** Plowing strength: 3 = riding mowers, which drive through sticks/brush and clear it. */
  cutPower: number;
  /**
   * Break strength: drives through (and smashes) lawn items up to this break tier, and crashes
   * into tougher ones (reel 0, push and self-propelled 1, zero-turn 2, tractor 3, Fairway 4).
   * Default 0: only the flimsiest things (a training cone).
   */
  breakPower?: number;
  /**
   * Grass height taken off per pass (see `grassHeight`): 1 = reel, 2 = engine mowers, +1 with
   * Sharpened Blades.
   */
  bite: number;
  /** Fuel tank in tiles of travel; undefined = no fuel (human-powered). */
  fuelTiles?: number;
  /** Tiles mowed side by side (default 1). Wide mowers need room to pivot. */
  cutWidth?: CutWidth;
  /**
   * Floating side reels (the Fairway Mower): each also mows the grass on the tile beside the mower,
   * left and right, and lifts over anything else (obstacles, flowers, critters) without a bump.
   */
  wings?: boolean;
  /** A slow gear the player can toggle for precision. */
  throttle?: { slowTicksPerTile: number };
  turnRule: TurnRuleSpec;
  /** Hardened Tires: punctures shrugged off per lawn before a thorn patch gives a flat. */
  tireGuard?: number;
  /** Bumper: crashes per lawn that cost no hit (the mower still bounces back). */
  bumper?: number;
  /** Reserve Can: times per lawn a dry tank fills up again by itself. */
  reserveCan?: number;
}

/** Player steering requests. Absolute (`face`) comes from swipes, relative (`turn`) from taps. */
export type Intent =
  | { type: 'turn'; turn: Turn }
  | { type: 'face'; dir: Direction }
  /** Throttle gear for mowers that have one (ignored otherwise). */
  | { type: 'throttle'; slow: boolean }
  /** Use the tool in a loadout slot. */
  | { type: 'tool'; slot: number }
  /** Fire a one-use boost (paid for outside the sim; once per kind per attempt). */
  | { type: 'boost'; boost: BoostId }
  /** Fire an item picked up from a box on this lawn (src/sim/items.ts). */
  | { type: 'item'; item: ItemId }
  /** Park at the next tile center and spin in place until steered (spinning mowers only). */
  | { type: 'park' }
  /**
   * Continue (paid for outside the sim): a broken or out-of-gas mower is put back in running order
   * where it stopped, with full health and a full tank, free to face any open way again.
   */
  | { type: 'revive' };

/** A steering request (everything but throttle, tools and revives). */
export type SteerIntent = Exclude<
  Intent,
  | { type: 'throttle' }
  | { type: 'tool' }
  | { type: 'boost' }
  | { type: 'item' }
  | { type: 'park' }
  | { type: 'revive' }
>;

export interface MowerState extends TurnRuleState {
  /** Tile the mower is at, or departing from while `progress > 0`. */
  x: number;
  y: number;
  dir: Direction;
  /** Ticks traveled toward the next tile; 0 means centered on (x, y). */
  progress: number;
  /**
   * Nosing toward a blocked tile (the turn grace at an obstacle, `BRINK_GRACE`): still deciding
   * at (x, y), and crashing once `progress` reaches the grace unless a turn comes first.
   */
  brink: boolean;
  hp: number;
  /** Tiles of fuel left (Infinity for human-powered mowers). */
  fuel: number;
  /** Ticks of post-crash invulnerability remaining. */
  invulnTicks: number;
  /** Buffered steering request awaiting a tile center where it can apply. */
  pendingIntent: SteerIntent | null;
  /**
   * A follow-up request that only makes sense after the pending one (e.g. the second half of a
   * U-turn: right, then down). Becomes pending once the pending turn is made.
   */
  queuedIntent: SteerIntent | null;
  /** Which side a wide mower's second track is on (fixed for the run). */
  side: Side;
  /** Throttled down to the slow gear. */
  slow: boolean;
  /** Tile centers the pending intent may still wait through before it expires. */
  intentCentersLeft: number;
  /**
   * Whether the mower has left its start tile. Until it has, it may be set facing any open
   * direction (it is being placed, not driven), free of its turn rule.
   */
  departed: boolean;
  /** Parked at a tile center, spinning in place until steered (or asked to at the next one). */
  parked: boolean;
  parkRequested: boolean;
  /**
   * Backing up to the tile center it just left, to park there (a hold that came in soon after
   * leaving it: see `Simulation.requestPark`). `progress` counts down while this is set.
   */
  backing: boolean;
  /** Ticks spent parked (spinning burns a little gas). */
  parkTicks: number;
  /** A flat tire (from a thorn patch): slow going until a repair kit patches it. */
  flat: boolean;
  /** Punctures Hardened Tires can still shrug off on this lawn. */
  tireGuard: number;
  /** Crashes the Bumper can still take on this lawn, and Reserve Can refills left. */
  bumper: number;
  reserveCan: number;
}

export function createMowerState(
  spec: MowerSpec,
  start: { x: number; y: number; dir: Direction },
  side: Side = 'left',
): MowerState {
  return {
    ...initialTurnRuleState(),
    x: start.x,
    y: start.y,
    dir: start.dir,
    parked: false,
    parkRequested: false,
    backing: false,
    parkTicks: 0,
    progress: 0,
    brink: false,
    hp: spec.maxHp,
    fuel: spec.fuelTiles ?? Number.POSITIVE_INFINITY,
    invulnTicks: 0,
    pendingIntent: null,
    queuedIntent: null,
    intentCentersLeft: 0,
    departed: false,
    side,
    slow: false,
    flat: false,
    tireGuard: spec.tireGuard ?? 0,
    bumper: spec.bumper ?? 0,
    reserveCan: spec.reserveCan ?? 0,
  };
}

export function validateMowerSpec(spec: MowerSpec): void {
  if (!Number.isInteger(spec.ticksPerTile) || spec.ticksPerTile < 2) {
    throw new RangeError(`${spec.id}: ticksPerTile must be an integer >= 2`);
  }
  if (!Number.isInteger(spec.bite) || spec.bite < 1) {
    throw new RangeError(`${spec.id}: bite must be a positive integer`);
  }
  if (!Number.isInteger(spec.cutPower) || spec.cutPower < 1) {
    throw new RangeError(`${spec.id}: cutPower must be a positive integer`);
  }
  if (spec.fuelTiles !== undefined && (!Number.isInteger(spec.fuelTiles) || spec.fuelTiles < 1)) {
    throw new RangeError(`${spec.id}: fuelTiles must be a positive integer`);
  }
  if (spec.cutWidth !== undefined && spec.cutWidth !== 1 && spec.cutWidth !== 2) {
    throw new RangeError(`${spec.id}: cutWidth must be 1 or 2`);
  }
  if (spec.throttle && spec.throttle.slowTicksPerTile <= spec.ticksPerTile) {
    throw new RangeError(`${spec.id}: the slow gear must be slower than normal`);
  }
  if (!Number.isInteger(spec.maxHp) || spec.maxHp < 1) {
    throw new RangeError(`${spec.id}: maxHp must be a positive integer`);
  }
}
